import type {
  ApproachTreeNode,
  ApproachTreeNodeType,
  ApproachTreeNodeData,
  ResolvedTypeMetadata,
} from 'types/approachExplorer';
import type {
  ApproachDefinition,
  ApproachChildTemplate,
  ApproachStatusFactorConfig,
  ApproachDeploymentInput,
  ApproachReference,
  ProvidedType,
  ProvidedContext,
} from 'types/approach';
import type { ArcTemplate, StageDefinition } from 'types/arc';
import type { ScheduleDefinition } from 'types/orbit';
import { resolveLocalizedString } from './resolveLocalizedString';

/** Maps for resolved display names, keyed by system name. */
export interface ResolvedNames {
  types?: Map<string, ResolvedTypeMetadata>;
  contexts?: Map<string, { displayName: string; description?: string }>;
}

/** Icon mapping per node type (PrimeIcons class names without 'pi pi-' prefix). */
const NODE_ICONS: Record<ApproachTreeNodeType, string> = {
  approach: 'pi-box',
  category: 'pi-th-large',
  'provided-type': 'pi-th-large',
  'provided-context': 'pi-chart-bar',
  'arc-stage': 'pi-sync',
  'child-template': 'pi-globe',
  'status-factor': 'pi-arrow-up-right',
  'orbit-schedule': 'pi-calendar',
  'deployment-input': 'pi-pencil',
  'sub-approach-ref': 'pi-hourglass',
};

/** Category keys in display order. */
const CATEGORY_KEYS = [
  'types',
  'contexts',
  'arc',
  'children',
  'factors',
  'orbit',
  'inputs',
  'subapproaches',
] as const;

/** Fallback labels when no translated labels are provided. */
const DEFAULT_CATEGORY_LABELS: Record<string, string> = {
  types: 'Sphere Types Unlocked',
  contexts: 'Contexts Unlocked',
  arc: 'Arc',
  children: 'What Gets Created',
  factors: 'Status Factors',
  orbit: 'Cadence / Orbit',
  inputs: 'Deployment Inputs',
  subapproaches: 'Included Approaches',
};

/**
 * Converts a system name into a human-readable display name.
 * Strips the `center_` prefix, replaces underscores with spaces, and title-cases each word.
 */
function formatSystemName(systemName: string): string {
  return systemName
    .replace(/^center_/, '')
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

// ── Node builders ──────────────────────────────────────────────────

function buildProvidedTypeNodes(
  types: ProvidedType[],
  prefix: string,
  resolvedNames?: ResolvedNames
): ApproachTreeNode[] {
  return types.map((pt) => {
    const resolved = resolvedNames?.types?.get(pt.schemaSystemName);
    const label = resolved?.displayName ?? formatSystemName(pt.schemaSystemName);
    const description = resolved?.description;
    const icon = resolved?.avatar || NODE_ICONS['provided-type'];
    const data: ApproachTreeNodeData = {
      type: 'provided-type',
      schemaSystemName: pt.schemaSystemName,
      displayName: label,
      description,
      isForked: pt.isForked,
      avatar: resolved?.avatar,
      properties: resolved?.properties,
      perspectives: resolved?.perspectives,
      useCases: resolved?.useCases,
    };
    return {
      id: `${prefix}.${pt.schemaSystemName}`,
      type: 'provided-type' as const,
      label,
      description,
      icon,
      data,
    };
  });
}

function buildProvidedContextNodes(
  contexts: ProvidedContext[],
  prefix: string,
  resolvedNames?: ResolvedNames
): ApproachTreeNode[] {
  return contexts.map((pc) => {
    const resolved = resolvedNames?.contexts?.get(pc.contextSystemName);
    const label = resolved?.displayName ?? formatSystemName(pc.contextSystemName);
    const description = resolved?.description;
    const data: ApproachTreeNodeData = {
      type: 'provided-context',
      contextSystemName: pc.contextSystemName,
      displayName: label,
      description,
    };
    return {
      id: `${prefix}.${pc.contextSystemName}`,
      type: 'provided-context' as const,
      label,
      description,
      icon: NODE_ICONS['provided-context'],
      data,
    };
  });
}

function buildArcStageNodes(
  stages: StageDefinition[],
  prefix: string,
  arcName?: string
): ApproachTreeNode[] {
  return stages.map((stage) => {
    const label = stage.fallbackName || formatSystemName(stage.id);
    const data: ApproachTreeNodeData = {
      type: 'arc-stage',
      stage,
      arcName,
    };
    return {
      id: `${prefix}.${stage.id}`,
      type: 'arc-stage' as const,
      label,
      icon: NODE_ICONS['arc-stage'],
      data,
    };
  });
}

function buildChildTemplateNodes(
  children: ApproachChildTemplate[],
  prefix: string,
  resolvedNames?: ResolvedNames
): ApproachTreeNode[] {
  return children.map((child) => {
    const label = resolveLocalizedString(child.name, formatSystemName(child.id));
    const resolved = resolvedNames?.types?.get(child.schemaSystemName);
    const icon = resolved?.avatar || NODE_ICONS['child-template'];
    const data: ApproachTreeNodeData = {
      type: 'child-template',
      child,
    };
    return {
      id: `${prefix}.${child.id}`,
      type: 'child-template' as const,
      label,
      icon,
      data,
    };
  });
}

function buildStatusFactorNodes(
  factors: ApproachStatusFactorConfig[],
  prefix: string
): ApproachTreeNode[] {
  return factors.map((factor) => {
    const label = resolveLocalizedString(factor.name, formatSystemName(factor.id));
    const data: ApproachTreeNodeData = {
      type: 'status-factor',
      factor,
    };
    return {
      id: `${prefix}.${factor.id}`,
      type: 'status-factor' as const,
      label,
      icon: NODE_ICONS['status-factor'],
      data,
    };
  });
}

function buildOrbitScheduleNodes(
  schedules: Record<string, ScheduleDefinition>,
  prefix: string,
  orbitSource?: string
): ApproachTreeNode[] {
  return Object.entries(schedules).map(([name, schedule]) => {
    const label = formatSystemName(name);
    const data: ApproachTreeNodeData = {
      type: 'orbit-schedule',
      schedule,
      orbitSource,
    };
    return {
      id: `${prefix}.${name}`,
      type: 'orbit-schedule' as const,
      label,
      icon: NODE_ICONS['orbit-schedule'],
      data,
    };
  });
}

function buildDeploymentInputNodes(
  inputs: ApproachDeploymentInput[],
  prefix: string
): ApproachTreeNode[] {
  return inputs.map((input) => {
    const label = resolveLocalizedString(input.label, formatSystemName(input.id));
    const data: ApproachTreeNodeData = {
      type: 'deployment-input',
      input,
    };
    return {
      id: `${prefix}.${input.id}`,
      type: 'deployment-input' as const,
      label,
      icon: NODE_ICONS['deployment-input'],
      data,
    };
  });
}

function buildSubApproachRefNodes(
  refs: ApproachReference[],
  prefix: string
): ApproachTreeNode[] {
  return refs.map((ref) => {
    const label = formatSystemName(ref.approachSystemName);
    const data: ApproachTreeNodeData = {
      type: 'sub-approach-ref',
      reference: ref,
    };
    return {
      id: `${prefix}.${ref.approachSystemName}`,
      type: 'sub-approach-ref' as const,
      label,
      icon: NODE_ICONS['sub-approach-ref'],
      expandable: true,
      data,
    };
  });
}

// ── Category builder ───────────────────────────────────────────────

function buildCategoryNode(
  key: string,
  label: string,
  prefix: string,
  children: ApproachTreeNode[]
): ApproachTreeNode | null {
  if (children.length === 0) return null;
  return {
    id: `${prefix}.${key}`,
    type: 'category',
    label,
    icon: NODE_ICONS.category,
    children,
  };
}

// ── Main export ────────────────────────────────────────────────────

/**
 * Transforms an approach definition into a flat-rooted tree of `ApproachTreeNode` nodes.
 *
 * Returns a single root node whose children are category nodes (one per non-empty
 * section of the definition). Empty sections are omitted.
 *
 * @param approach - The approach metadata and definition to visualize.
 * @param arcTemplate - Optional resolved flow template (provides stage definitions).
 * @param resolvedNames - Optional pre-resolved display names for types and contexts.
 * @param nodeIdPrefix - ID prefix for the root node (default `"root"`). Used when
 *                       building sub-trees for expanded sub-approaches.
 * @param categoryLabels - Optional translated category labels keyed by category key.
 *                         When omitted, English fallback labels are used.
 * @returns A single `ApproachTreeNode` representing the approach root with category children.
 */
export function buildApproachTree(
  approach: {
    systemName: string;
    name: string;
    description?: string;
    definition: ApproachDefinition;
  },
  arcTemplate?: ArcTemplate | null,
  resolvedNames?: ResolvedNames,
  nodeIdPrefix = 'root',
  categoryLabels?: Record<string, string>
): ApproachTreeNode {
  const def = approach.definition;
  const categoryNodes: ApproachTreeNode[] = [];
  const labels = categoryLabels ?? DEFAULT_CATEGORY_LABELS;

  // Provided Types
  if (def.providedTypes && def.providedTypes.length > 0) {
    const cat = buildCategoryNode(
      'types',
      labels['types'] ?? DEFAULT_CATEGORY_LABELS['types'],
      nodeIdPrefix,
      buildProvidedTypeNodes(def.providedTypes, `${nodeIdPrefix}.types`, resolvedNames)
    );
    if (cat) categoryNodes.push(cat);
  }

  // Provided Contexts
  if (def.providedContexts && def.providedContexts.length > 0) {
    const cat = buildCategoryNode(
      'contexts',
      labels['contexts'] ?? DEFAULT_CATEGORY_LABELS['contexts'],
      nodeIdPrefix,
      buildProvidedContextNodes(def.providedContexts, `${nodeIdPrefix}.contexts`, resolvedNames)
    );
    if (cat) categoryNodes.push(cat);
  }

  // Arc — use arc template stages if available
  const stages = arcTemplate?.definition?.stages;
  if (stages && stages.length > 0) {
    const cat = buildCategoryNode(
      'arc',
      labels['arc'] ?? DEFAULT_CATEGORY_LABELS['arc'],
      nodeIdPrefix,
      buildArcStageNodes(stages, `${nodeIdPrefix}.arc`, arcTemplate?.name)
    );
    if (cat) categoryNodes.push(cat);
  }

  // What Gets Created (children)
  if (def.children && def.children.length > 0) {
    const cat = buildCategoryNode(
      'children',
      labels['children'] ?? DEFAULT_CATEGORY_LABELS['children'],
      nodeIdPrefix,
      buildChildTemplateNodes(def.children, `${nodeIdPrefix}.children`, resolvedNames)
    );
    if (cat) categoryNodes.push(cat);
  }

  // Status Factors
  if (def.statusFactors && def.statusFactors.length > 0) {
    const cat = buildCategoryNode(
      'factors',
      labels['factors'] ?? DEFAULT_CATEGORY_LABELS['factors'],
      nodeIdPrefix,
      buildStatusFactorNodes(def.statusFactors, `${nodeIdPrefix}.factors`)
    );
    if (cat) categoryNodes.push(cat);
  }

  // Cadence / Orbit
  const orbitSchedules = def.orbit?.inlineDefinition?.schedules;
  if (orbitSchedules && Object.keys(orbitSchedules).length > 0) {
    const cat = buildCategoryNode(
      'orbit',
      labels['orbit'] ?? DEFAULT_CATEGORY_LABELS['orbit'],
      nodeIdPrefix,
      buildOrbitScheduleNodes(orbitSchedules, `${nodeIdPrefix}.orbit`, def.orbit?.source)
    );
    if (cat) categoryNodes.push(cat);
  }

  // Deployment Inputs
  if (def.deploymentInputs && def.deploymentInputs.length > 0) {
    const cat = buildCategoryNode(
      'inputs',
      labels['inputs'] ?? DEFAULT_CATEGORY_LABELS['inputs'],
      nodeIdPrefix,
      buildDeploymentInputNodes(def.deploymentInputs, `${nodeIdPrefix}.inputs`)
    );
    if (cat) categoryNodes.push(cat);
  }

  // Included Approaches (sub-approach references)
  if (def.subApproaches && def.subApproaches.length > 0) {
    const cat = buildCategoryNode(
      'subapproaches',
      labels['subapproaches'] ?? DEFAULT_CATEGORY_LABELS['subapproaches'],
      nodeIdPrefix,
      buildSubApproachRefNodes(def.subApproaches, `${nodeIdPrefix}.subapproaches`)
    );
    if (cat) categoryNodes.push(cat);
  }

  // Root approach node
  const rootData: ApproachTreeNodeData = {
    type: 'approach',
    definition: def,
    systemName: approach.systemName,
  };

  return {
    id: nodeIdPrefix,
    type: 'approach',
    label: approach.name,
    description: approach.description,
    icon: NODE_ICONS.approach,
    children: categoryNodes,
    expandable: true,
    data: rootData,
  };
}
